import React, { 
  useCallback, 
  useRef, 
  useEffect, 
  useMemo, 
  useState,
} from "react";
import { compact } from "lodash";

import { 
  useForm, 
  useWatch, 
  Controller,
} from "react-hook-form-7";

import { 
  CheckCircleIcon, 
  CollaboratorsIcon,
} from "../components/icons";

import useTranslations, { TranslationKey } from "../hooks/useTranslations";

import useViewer from "../hooks/useViewer";

import {
  Button as WombatButton,
  ScreenHeader,
  Container,
  Input,
} from "../components/wombat-ui";
import useNotification from "../hooks/useNotification";
import UserAvatar from "../components/utils/avatars/UserAvatar";
import {
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  RadioGroup,
  Radio,
  Stack,
  StackDivider,
  Text,
  Switch,
} from "@chakra-ui/react";
import Notice from "../components/utils/Notice";
import { useHistory } from "react-router";
import {
  GetUsersMinimalDocument,
  GetViewerDocument,
  useDeleteAvatar,
  useInsertUser,
  UserReviewFragment,
} from "../graphql/generated/definitions";
import useEditAvatar from "../hooks/useEditAvatar";
import { XCircleIcon } from "@heroicons/react/outline";

type Right = {
  titleTranslationKey: TranslationKey;
  descriptionTranslationKey?: TranslationKey;
  formKey: keyof UserInput["capabilities"];
  rightsOptions: {
    label: TranslationKey;
    key: string;
  }[];
};

type UserInput = {
  id: string;
  name: string;
  email: string;
  enabled: boolean;
  locale: string;
  capabilities: {
    groups: string;
    professions: boolean;
    reviews: string;
    reviewsAll: boolean;
    campaigns: string;
    companies: boolean;
    templates: string;
    users: boolean;
    activities: boolean;
    translations: boolean;
    standaloneQuestions: string;
    standaloneAnswers: string;
    userNotes: string;
    documents: boolean;
    manageReport: boolean;
    sign: boolean;
    capabilitiesManagement: boolean;
  };
  avatar: {
    filename: string;
    data: string;
  };
};

const LOCALE_OPTIONS = [
  { value: "fr-FR", label: "Francais" },
  { value: "en-US", label: "English" },
];

type UserFormProps = {
  defaultValues?: UserReviewFragment;
};

const getCapabilitiesConfig = (
  canSign: boolean,
  canTranslate: boolean
): Array<Right>[] => [
  [
    {
      formKey: "groups",
      titleTranslationKey: "users.capabilities.groups.crud.title",
      descriptionTranslationKey: "users.capabilities.groups.crud.description",
      rightsOptions: [
        { key: "none", label: "users.capabilities.groups.crud.none" },
        {
          key: "inserting_groups",
          label: "users.capabilities.groups.crud.insert",
        },
        {
          key: "deleting_groups",
          label: "users.capabilities.groups.crud.delete",
        },
      ],
    },
  ],
  [
    {
      formKey: "professions",
      titleTranslationKey:
        "users.capabilities.librairies.manage_librairies.title",
      descriptionTranslationKey:
        "users.capabilities.librairies.manage_librairies.description",
      rightsOptions: [
        {
          key: "inserting_professions",
          label: "users.capabilities.librairies.manage_librairies.title",
        },
      ],
    },
  ],
  [
    {
      formKey: "reviews",
      titleTranslationKey: "users.capabilities.reviews.crud.title",
      descriptionTranslationKey: "users.capabilities.reviews.crud.description",
      rightsOptions: [
        { key: "none", label: "users.capabilities.reviews.crud.none" },
        {
          key: "inserting_reviews",
          label: "users.capabilities.reviews.crud.insert",
        },
        {
          key: "deleting_reviews",
          label: "users.capabilities.reviews.crud.delete",
        },
      ],
    },
    {
      formKey: "reviewsAll",
      titleTranslationKey: "users.capabilities.reviews.allReviews.title",
      descriptionTranslationKey:
        "users.capabilities.reviews.allReviews.description",
      rightsOptions: [
        {
          key: "reading_all_reviews",
          label: "users.capabilities.reviews.allReviews.title",
        },
      ],
    },
  ],
  [
    {
      formKey: "campaigns",
      titleTranslationKey: "users.capabilities.campaigns.crud.title",
      descriptionTranslationKey:
        "users.capabilities.campaigns.crud.description",
      rightsOptions: [
        { key: "none", label: "users.capabilities.campaigns.crud.none" },
        {
          key: "reading_campaigns",
          label: "users.capabilities.campaigns.crud.read",
        },
        {
          key: "inserting_campaigns",
          label: "users.capabilities.campaigns.crud.insert",
        },
        {
          key: "deleting_campaigns",
          label: "users.capabilities.campaigns.crud.delete",
        },
      ],
    },
  ],
  compact([
    {
      formKey: "companies",
      titleTranslationKey: "users.capabilities.company.update.title",
      descriptionTranslationKey:
        "users.capabilities.company.update.description",
      rightsOptions: [
        {
          key: "updating_companies",
          label: "users.capabilities.company.update.title",
        },
      ],
    },
    {
      formKey: "activities",
      titleTranslationKey: "users.capabilities.company.activities.title",
      descriptionTranslationKey:
        "users.capabilities.company.activities.description",
      rightsOptions: [
        {
          key: "reading_activities",
          label: "users.capabilities.company.activities.title",
        },
      ],
    },
    canTranslate && {
      formKey: "translations",
      titleTranslationKey: "users.capabilities.company.translations.title",
      descriptionTranslationKey:
        "users.capabilities.company.translations.description",
      rightsOptions: [
        {
          key: "inserting_translations",
          label: "users.capabilities.company.translations.title",
        },
      ],
    },
  ]),
  [
    {
      formKey: "templates",
      titleTranslationKey: "users.capabilities.templates.crud.title",
      descriptionTranslationKey:
        "users.capabilities.templates.crud.description",
      rightsOptions: [
        { key: "none", label: "users.capabilities.templates.crud.none" },
        {
          key: "inserting_templates",
          label: "users.capabilities.templates.crud.insert",
        },
        {
          key: "deleting_templates",
          label: "users.capabilities.templates.crud.delete",
        },
      ],
    },
  ],
  [
    {
      formKey: "users",
      titleTranslationKey: "users.capabilities.users.insert.title",
      descriptionTranslationKey: "users.capabilities.users.insert.description",
      rightsOptions: [
        {
          key: "inserting_users",
          label: "users.capabilities.users.insert.title",
        },
      ],
    },
    {
      formKey: "userNotes",
      titleTranslationKey: "users.capabilities.users.notes.crud.title",
      descriptionTranslationKey:
        "users.capabilities.users.notes.crud.description",
      rightsOptions: [
        { key: "none", label: "users.capabilities.users.notes.crud.none" },
        {
          key: "reading_user_notes",
          label: "users.capabilities.users.notes.crud.read",
        },
        {
          key: "updating_user_notes",
          label: "users.capabilities.users.notes.crud.insert",
        },
      ],
    },
    {
      formKey: "documents",
      titleTranslationKey: "users.capabilities.users.documents.crud.title",
      descriptionTranslationKey:
        "users.capabilities.users.documents.crud.description",
      rightsOptions: [
        {
          key: "none",
          label: "users.capabilities.users.documents.crud.none",
        },
        {
          key: "reading_user_documents",
          label: "users.capabilities.users.documents.crud.read",
        },
      ],
    },
  ],
  [
    {
      formKey: "standaloneAnswers",
      titleTranslationKey:
        "users.capabilities.grading.standaloneAnswers.crud.title",
      descriptionTranslationKey:
        "users.capabilities.grading.standaloneAnswers.crud.description",
      rightsOptions: [
        {
          key: "none",
          label: "users.capabilities.grading.standaloneAnswers.crud.none",
        },
        {
          key: "reading_standalone_answers",
          label: "users.capabilities.grading.standaloneAnswers.crud.read",
        },
        {
          key: "creating_standalone_answers_for_others",
          label: "users.capabilities.grading.standaloneAnswers.crud.create",
        },
        // deleting_answers?
        // Droit de supprimer les réponses
      ],
    },
    {
      formKey: "standaloneQuestions",
      titleTranslationKey:
        "users.capabilities.grading.standaloneQuestions.crud.title",
      descriptionTranslationKey:
        "users.capabilities.grading.standaloneQuestions.crud.description",
      rightsOptions: [
        {
          key: "none",
          label: "users.capabilities.grading.standaloneQuestions.crud.none",
        },
        {
          key: "reading_standalone_questions",
          label: "users.capabilities.grading.standaloneQuestions.crud.read",
        },
        {
          key: "creating_standalone_questions_for_others",
          label: "users.capabilities.grading.standaloneQuestions.crud.create",
        },
      ],
    },
  ],
  compact([
    canSign && {
      formKey: "sign",
      titleTranslationKey: "users.capabilities.reports.signing.title",
      descriptionTranslationKey:
        "users.capabilities.reports.signing.description",
      rightsOptions: [
        {
          key: "signing_reports",
          label: "users.capabilities.reports.signing.title",
        },
      ],
    },
    {
      formKey: "manageReport",
      titleTranslationKey: "users.capabilities.reports.manage.title",
      descriptionTranslationKey:
        "users.capabilities.reports.manage.description",
      rightsOptions: [
        {
          key: "managing_reports",
          label: "users.capabilities.reports.manage.title",
        },
      ],
    },
  ]),
  [
    {
      formKey: "capabilitiesManagement",
      titleTranslationKey:
        "users.capabilities.advanced.updateCapabilities.title",
      descriptionTranslationKey:
        "users.capabilities.advanced.updateCapabilities.description",
      rightsOptions: [
        {
          key: "updating_user_capabilities",
          label: "users.capabilities.advanced.updateCapabilities.title",
        },
      ],
    },
  ],
];

function getDefaultCapabilities(
  locale: string,
  defaultValues?: UserReviewFragment
): UserInput {
  const getGroupsCapabilities = (canUpdate: boolean, canDelete: boolean) => {
    if (canDelete) return "deleting_groups";
    else if (canUpdate) return "inserting_groups";
    return "none";
  };
  const getReviewsCapabilities = (canUpdate: boolean, canDelete: boolean) => {
    if (canDelete) return "deleting_reviews";
    else if (canUpdate) return "inserting_reviews";
    return "none";
  };
  const getCampaignsCapabilities = (canUpdate: boolean, canDelete: boolean) => {
    if (canDelete) return "deleting_campaigns";
    else if (canUpdate) return "inserting_campaigns";
    return "none";
  };
  const getTemplatesCapabilities = (canUpdate: boolean, canDelete: boolean) => {
    if (canDelete) return "deleting_templates";
    else if (canUpdate) return "inserting_templates";
    return "none";
  };
  const getStandaloneQuestionsCapabilities = (
    canRead: boolean,
    canCreate: boolean
  ) => {
    if (canCreate) return "creating_standalone_questions_for_others";
    else if (canRead) return "reading_standalone_questions";
    return "none";
  };
  const getStandaloneAnswersCapabilities = (
    canRead: boolean,
    canCreate: boolean
  ) => {
    if (canCreate) return "creating_standalone_answers_for_others";
    else if (canRead) return "reading_standalone_answers";
    return "none";
  };
  const getStandaloneUserNoteCapabilities = (
    canRead: boolean,
    canUpdate: boolean
  ) => {
    if (canUpdate) return "updating_user_notes";
    else if (canRead) return "reading_user_notes";
    return "none";
  };

  return {
    id: defaultValues?.id,
    email: defaultValues?.email,
    name: defaultValues?.name,
    enabled: defaultValues?.enabled,
    locale,
    avatar: {
      filename: "",
      data: defaultValues?.avatar || null,
    },
    capabilities: {
      groups: getGroupsCapabilities(
        defaultValues?.isCapableOfInsertingGroups,
        defaultValues?.isCapableOfDeletingGroups
      ),
      professions: defaultValues?.isCapableOfInsertingProfessions,
      reviews: getReviewsCapabilities(
        defaultValues?.isCapableOfInsertingReviews,
        defaultValues?.isCapableOfDeletingReviews
      ),
      reviewsAll: defaultValues?.isCapableOfReadingAllReviews,
      campaigns: getCampaignsCapabilities(
        defaultValues?.isCapableOfInsertingCampaigns,
        defaultValues?.isCapableOfDeletingCampaigns
      ),
      companies: defaultValues?.isCapableOfUpdatingCompanies,
      templates: getTemplatesCapabilities(
        defaultValues?.isCapableOfInsertingTemplates,
        defaultValues?.isCapableOfDeletingTemplates
      ),
      users: defaultValues?.isCapableOfInsertingUsers,
      activities: defaultValues?.isCapableOfReadingActivities,
      translations: defaultValues?.isCapableOfInsertingTranslations,
      standaloneQuestions: getStandaloneQuestionsCapabilities(
        defaultValues?.isCapableOfReadingStandaloneQuestions,
        defaultValues?.isCapableOfCreatingStandaloneQuestions
      ),
      standaloneAnswers: getStandaloneAnswersCapabilities(
        defaultValues?.isCapableOfReadingStandaloneAnswers,
        defaultValues?.isCapableOfCreatingStandaloneAnswers
      ),
      userNotes: getStandaloneUserNoteCapabilities(
        defaultValues?.isCapableOfReadingUserNotes,
        defaultValues?.isCapableOfUpdatingUserNotes
      ),
      documents: defaultValues?.isCapableOfReadingUserDocuments,
      manageReport: defaultValues?.isCapableOfManagingReports,
      sign: defaultValues?.isCapableOfSigningReports,
      capabilitiesManagement:
        defaultValues?.isCapableOfUpdatingUserCapabilities,
    },
  };
}

const UserFormScreen: React.FC<UserFormProps> = ({ defaultValues }) => {
  const fileRef = useRef<{ filename: string; data: string }>(null);

  const {
    company: {
      id: companyId,
      customTranslationsEnabled,
      reportSigningEnabled,
    },
    createdAt,
    isCapableOfUpdatingUserCapabilities,
    locale
  } = useViewer();

  const { notifySuccess } = useNotification();
  const t = useTranslations();

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = useForm<UserInput>({
    defaultValues: { ...getDefaultCapabilities(defaultValues?.locale || locale, defaultValues) },
  });

  const history = useHistory();
  const [insertUser, { loading }] = useInsertUser();

  const capabilitiesConfig = useMemo(
    () =>
      getCapabilitiesConfig(reportSigningEnabled, customTranslationsEnabled),
    [customTranslationsEnabled, reportSigningEnabled]
  );

  const [deleteAvatar] = useDeleteAvatar();

  const onSubmit = useCallback(
    async ({ name, email, locale, enabled, avatar, capabilities }: UserInput) => {
      if (defaultValues.id && avatar.data === "") {
        await deleteAvatar({
          variables: {
            input: {
              userId: defaultValues.id,
            },
          },
        });
      }

      await insertUser({
        variables: {
          input: {
            id: defaultValues.id,
            name,
            email,
            locale,
            enabled,
            avatar: avatar.filename !== "" ? avatar.data : null,
            companyId,

            // Only send capabilities if user can update them
            ...(isCapableOfUpdatingUserCapabilities
              ? {
                  // Groups

                  // FIXME: Case en plus ?
                  isCapableOfInsertingGroupMembers:
                    capabilities.groups === "inserting_groups" ||
                    capabilities.groups === "deleting_groups",

                  isCapableOfInsertingGroups:
                    capabilities.groups === "inserting_groups" ||
                    capabilities.groups === "deleting_groups",
                  isCapableOfDeletingGroups:
                    capabilities.groups === "deleting_groups",

                  // Professions
                  isCapableOfReadingSkills:
                    capabilities.professions,
                  isCapableOfInsertingProfessions: capabilities.professions,
                  isCapableOfDeletingProfessions: capabilities.professions,
                  isCapableOfInsertingProfessionSkills:
                    capabilities.professions,
                  isCapableOfInsertingSkills: capabilities.professions,
                  isCapableOfDeletingSkills: capabilities.professions,

                  // Users
                  isCapableOfInsertingUsers: capabilities.users,
                  isCapableOfReadingUserNotes:
                    capabilities.userNotes === "reading_user_notes" ||
                    capabilities.userNotes === "updating_user_notes",
                  isCapableOfUpdatingUserNotes:
                    capabilities.userNotes === "updating_user_notes",
                  isCapableOfReadingUserDocuments: capabilities.documents,

                  // Companies
                  isCapableOfUpdatingCompanies: capabilities.companies,
                  isCapableOfInsertingTranslations: capabilities.translations,
                  isCapableOfReadingActivities: capabilities.activities,

                  // Reviews
                  isCapableOfInsertingReviews:
                    capabilities.reviews === "inserting_reviews" ||
                    capabilities.reviews === "deleting_reviews",
                  isCapableOfDeletingReviews:
                    capabilities.reviews === "deleting_reviews",
                  isCapableOfReadingAllReviews: capabilities.reviewsAll,
                  isCapableOfReadingQuestions: capabilities.reviewsAll,

                  // Campaigns
                  isCapableOfReadingCampaigns:
                    capabilities.campaigns === "reading_campaigns" ||
                    capabilities.campaigns === "inserting_campaigns" ||
                    capabilities.campaigns === "deleting_campaigns",
                  isCapableOfInsertingCampaigns:
                    capabilities.campaigns === "inserting_campaigns" ||
                    capabilities.campaigns === "deleting_campaigns",
                  isCapableOfDeletingCampaigns:
                    capabilities.campaigns === "deleting_campaigns",

                  // Templates
                  isCapableOfInsertingTemplates:
                    capabilities.templates === "inserting_templates" ||
                    capabilities.templates === "deleting_templates",
                  isCapableOfDeletingTemplates:
                    capabilities.templates === "deleting_templates",

                  // Reports
                  isCapableOfManagingReports: capabilities.manageReport,
                  isCapableOfSigningReports: capabilities.sign,

                  // Grading
                  isCapableOfReadingStandaloneAnswers:
                    capabilities.standaloneAnswers ===
                      "reading_standalone_answers" ||
                    capabilities.standaloneAnswers ===
                      "creating_standalone_answers_for_others",
                  isCapableOfCreatingStandaloneAnswers:
                    capabilities.standaloneAnswers ===
                    "creating_standalone_answers_for_others",
                  isCapableOfReadingStandaloneQuestions:
                    capabilities.standaloneQuestions ===
                      "reading_standalone_questions" ||
                    capabilities.standaloneQuestions ===
                      "creating_standalone_questions_for_others",
                  isCapableOfCreatingStandaloneQuestions:
                    capabilities.standaloneQuestions ===
                    "creating_standalone_questions_for_others",

                  // Advanced
                  isCapableOfUpdatingUserCapabilities:
                    capabilities.capabilitiesManagement,
                }
              : {}),
          },
        },
        refetchQueries: [
          { query: GetViewerDocument },
          {
            query: GetUsersMinimalDocument,
            variables: { page: 1, per: 30, sortBy: "name" },
          },
        ],
        awaitRefetchQueries: true,
      });

      history.push(`/users`);

      notifySuccess({
        title: t("userProfile.notification.success") as string,
      });
    },
    [defaultValues.id]
  );

  useEffect(() => {
    register(`avatar.filename`);
    register(`avatar.data`);
  }, [errors]);

  const avatarData = useWatch({
    control,
    name: `avatar.data`,
    defaultValue: defaultValues?.avatar?.data,
  });

  const editAvatar = useEditAvatar();
  const handleAvatarChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target?.files?.[0];

      editAvatar({
        title: t("users.avatar") as string,
        file,
        onComplete: (result) => {
          setValue(`avatar.filename`, file.name);
          setValue(`avatar.data`, result);
          fileRef.current = {
            filename: file.name,
            data: result as string,
          };
        },
      });
    },
    [editAvatar]
  );

  const handleDeleteAvatar = useCallback(() => {
    setValue(`avatar.filename`, "");
    setValue(`avatar.data`, "");
  }, [defaultValues.id]);

  const [isCapabilitiesAreAllChecked, setIsCapabilitiesAreAllChecked] = useState(false);

  const checkAllCapabilitiesButtonText = useMemo(() => {
    if (isCapabilitiesAreAllChecked)
      return t("users.capabilities.allCapabilitiesAreCheckedTextButton");
    else
      return t("users.capabilities.allCapabilitiesAreUncheckedTextButton");
  }, 
  [isCapabilitiesAreAllChecked]);

  const checkAllCapabilitiesDescriptionText = useMemo(() => {
    if (isCapabilitiesAreAllChecked)
      return t("users.capabilities.allCapabilitiesAreCheckedTextDescription");
    else
      return t("users.capabilities.allCapabilitiesAreUncheckedTextDescription");
  }, 
  [isCapabilitiesAreAllChecked]);

  const checkAllCapabilitiesButtonIcon = useMemo(() => {
    if (isCapabilitiesAreAllChecked)
      return (<XCircleIcon className="h-5 w-5"/>);
    else
      return (<CheckCircleIcon className="h-5 w-5"/>);
  }, 
  [isCapabilitiesAreAllChecked]);

  const capabilitiesValues = watch("capabilities");
  useEffect(() => {
    if (
      capabilitiesValues.activities &&
      capabilitiesValues.campaigns === "deleting_campaigns" &&
      capabilitiesValues.capabilitiesManagement &&
      capabilitiesValues.companies  &&
      capabilitiesValues.documents &&
      capabilitiesValues.groups === "deleting_groups" &&
      capabilitiesValues.manageReport &&
      capabilitiesValues.professions &&
      capabilitiesValues.reviews === "deleting_reviews" &&
      capabilitiesValues.reviewsAll &&
      (
        (reportSigningEnabled && capabilitiesValues.sign) || 
        !reportSigningEnabled
      ) &&
      capabilitiesValues.standaloneAnswers === "creating_standalone_answers_for_others" &&
      capabilitiesValues.standaloneQuestions === "creating_standalone_questions_for_others" &&
      capabilitiesValues.templates === "deleting_templates" &&
      (
        (customTranslationsEnabled && capabilitiesValues.translations) || 
        !customTranslationsEnabled
      ) &&
      capabilitiesValues.userNotes === "updating_user_notes" &&
      capabilitiesValues.users
      ) {
        setIsCapabilitiesAreAllChecked(true);
      } else {
        setIsCapabilitiesAreAllChecked(false);
      } 

      console.log(capabilitiesValues);
  },
  [capabilitiesValues, setIsCapabilitiesAreAllChecked]);

  const handleCheckAllCapabilitiesButtonOnClick = useCallback(() => {
    setIsCapabilitiesAreAllChecked(!isCapabilitiesAreAllChecked);

    if (!isCapabilitiesAreAllChecked) {
      notifySuccess({
        title: t(
          "users.capabilities.allCapabilitiesAreCheckedNotification"
        ),
      });
      setValue("capabilities", {
        groups: "deleting_groups",
        professions: true,
        reviews: "deleting_reviews",
        reviewsAll: true,
        campaigns: "deleting_campaigns",
        companies: true,
        templates: "deleting_templates",
        users: true,
        activities: true,
        translations: true,
        standaloneQuestions:
          "creating_standalone_questions_for_others",
        standaloneAnswers:
          "creating_standalone_answers_for_others",
        userNotes: "updating_user_notes",
        documents: true,
        manageReport: true,
        sign: true,
        capabilitiesManagement: true,
      });
    } else {
      notifySuccess({
        title: t(
          "users.capabilities.allCapabilitiesAreUncheckedNotification"
        ),
      });
      setValue("capabilities", {
        groups: "none",
        professions: false,
        reviews: "none",
        reviewsAll: false,
        campaigns: "none",
        companies: false,
        templates: "none",
        users: false,
        activities: false,
        translations: false,
        standaloneQuestions:
          "none",
        standaloneAnswers:
          "none",
        userNotes: "none",
        documents: false,
        manageReport: false,
        sign: false,
        capabilitiesManagement: false,
      });
    }
  },
  [isCapabilitiesAreAllChecked, setIsCapabilitiesAreAllChecked, setValue, notifySuccess]);

  return (
    <>
      <ScreenHeader
        icon={CollaboratorsIcon}
        title={
          defaultValues.name
            ? defaultValues.name
            : (t("collaborators.new") as string)
        }
        actions={[
          <WombatButton
            key="submit"
            type="submit"
            variant="primary"
            form="addUserForm"
            loading={loading}
          >
            {t("forms.actions.save")}
          </WombatButton>,
        ]}
      />
      <Container className="bg-white">
        <form
          className="flex flex-col"
          id="addUserForm"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Heading as="h4" size="md">
            Informations
          </Heading>
          <div>
            <label className="block mt-6 text-sm font-medium leading-5 text-gray-700">
              {t("users.avatar")}
            </label>
            <div className="flex items-center mt-2">
              <UserAvatar size={12} src={avatarData} alt="selected-avatar" />
              <span className="relative ml-5 overflow-hidden rounded-md shadow-sm">
                <Button type="button" size="xs">
                  {t("forms.actions.change")}
                </Button>
                <input
                  className="absolute top-0 right-0 block py-2 opacity-0 cursor-pointer"
                  type="file"
                  onChange={handleAvatarChange}
                  accept="image/png, image/jpeg, image/jpg"
                  name="avatar"
                />
              </span>
              {avatarData && (
                <Button
                  type="button"
                  onClick={handleDeleteAvatar}
                  colorScheme="red"
                  size="xs"
                  ml={3}
                  variant="outline"
                  data-test-id="deleteAvatarButton"
                >
                  {t("forms.actions.delete")}
                </Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 col-span-4 my-6 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <Input
                id="name"
                label={t("users.name") as string}
                error={errors?.name}
                {...register("name", {
                  required: t("forms.errors.required") as string,
                })}
                defaultValue={defaultValues.name}
              />
            </div>
            <div className="sm:col-span-4">
              <Input
                id="email"
                label={t("users.email") as string}
                error={errors?.email}
                {...register("email", {
                  required: t("forms.errors.required") as string,
                })}
                type="email"
                defaultValue={defaultValues.email}
              />
            </div>
          </div>
          
          <FormControl 
            display="flex" 
            alignItems="center"
          >
            <FormLabel 
              htmlFor="enabled-switch" 
              mb="1"
            >
              {t("users.enabled")}
            </FormLabel>
            <Controller
              control={control}
              name={`enabled`}
              render={({ field: { value, ...restField } }) => (
                <Switch 
                  id="enabled-switch" 
                  data-test-id="user-enabled-switch" 
                  isChecked={value} 
                  {...restField}
                />
              )}
            />
          </FormControl>

          <FormControl 
            mt="3"
          >
            <FormLabel>
              {t("users.locale")}
            </FormLabel>
            <Controller
              control={control}
              name={`locale`}
              render={({ field: { value, ...restField } }) => (
                <RadioGroup value={value as string} {...restField}>
                  <Stack direction="row" spacing="4">
                    {LOCALE_OPTIONS.map(({ value, label }) => (
                      <Radio size="sm" value={value} key={value}>
                        {label}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              )}
            />
          </FormControl>
          
          <Box hidden={!isCapableOfUpdatingUserCapabilities} mt="10">
            <Heading as="h4" size="md">
              {t("users.capabilities.title")}
            </Heading>
            <Text color="gray.500" mt="1">
              {t("users.capabilities.titleDescription")}
            </Text>

            <Notice
              noticeId="398-399-roles-update"
              viewerCreatedAt={createdAt}
              createdAt={"9/11/2021"}
              expireAt={"9/12/2021"}
              status="info"
              my="2"
            >
              <AlertIcon />
              <Box flex="1">
                <AlertTitle>
                  {t("users.capabilities.398-notice.title")}
                </AlertTitle>
                <AlertDescription display="block">
                  {t("users.capabilities.398-notice.description")}
                </AlertDescription>
              </Box>
            </Notice>
            
            <Stack 
              direction="row" 
              mt="8" 
              spacing="6" 
              justifyContent="space-between"
              alignItems="center"
            >
              <Text
                fontWeight="semibold"
                flexShrink={1}
              >
                {checkAllCapabilitiesDescriptionText}
              </Text>
              
              <Button
                id="checkAllCapabilitiesButton"
                backgroundColor="transparent"
                borderColor="secondary"
                flexShrink={0}
                borderWidth="thin"
                leftIcon={checkAllCapabilitiesButtonIcon}
                onClick={handleCheckAllCapabilitiesButtonOnClick}
              >
                {checkAllCapabilitiesButtonText}
              </Button>
            </Stack>

            <Stack
              direction="column"
              mt="6"
              spacing="0"
              divider={<StackDivider borderColor="gray.200" />}
            >
              {capabilitiesConfig.map((rights) =>
                rights.map(
                  (
                    {
                      formKey,
                      titleTranslationKey,
                      descriptionTranslationKey,
                      rightsOptions,
                    },
                    index
                  ) => (
                    <Flex
                      py="4"
                      flexGrow={1}
                      flexDir="row"
                      alignItems="start"
                      as="label"
                      htmlFor={formKey}
                      key={`${formKey}-${index}`}
                      _hover={{ background: "gray.50" }}
                    >
                      {rightsOptions.length <= 2 && (
                        <FormControl w="max-content" mr="3" mt="1">
                          <FormLabel htmlFor={formKey} srOnly>
                            {t(titleTranslationKey)}
                          </FormLabel>
                          <Controller
                            control={control}
                            name={`capabilities.${formKey}`}
                            render={({ field: { value, ...restField } }) => (
                              <Checkbox
                                isChecked={!!value}
                                {...restField}
                                id={formKey}
                              />
                            )}
                          />
                        </FormControl>
                      )}
                      <Flex direction="column" mr="8">
                        <Text fontSize="sm" fontWeight="semibold">
                          {t(titleTranslationKey)}
                        </Text>
                        {descriptionTranslationKey && (
                          <Text fontSize="xs" color="gray.500">
                            {t(descriptionTranslationKey)}
                          </Text>
                        )}
                        {rightsOptions.length > 2 && (
                          <FormControl mt="2">
                            <FormLabel htmlFor={formKey} srOnly>
                              {t(titleTranslationKey)}
                            </FormLabel>
                            <Controller
                              control={control}
                              name={`capabilities.${formKey}`}
                              render={({ field: { value, ...restField } }) => (
                                <RadioGroup
                                  value={value as string}
                                  {...restField}
                                >
                                  <Stack
                                    direction={{ base: "column", lg: "row" }}
                                    spacing={{ base: "1", lg: "4" }}
                                  >
                                    {rightsOptions.map(({ key, label }) => (
                                      <Radio size="sm" value={key} key={key}>
                                        {t(label)}
                                      </Radio>
                                    ))}
                                  </Stack>
                                </RadioGroup>
                              )}
                            />
                          </FormControl>
                        )}
                      </Flex>
                    </Flex>
                  )
                )
              )}
            </Stack>
          </Box>
        </form>
      </Container>
    </>
  );
};

export default UserFormScreen;
